import { useState, useCallback } from 'react';
import showToastPromise from '../utils/toast-promises.js';
import UserFriendlyError from '../utils/UserFriendlyError.js';
import callApi from '../utils/call-api.js';
import analyzeApiResponse from '../utils/analyze-api-response.js';
import convertError from '../utils/error-converter.js';

const useSystemRequirements = () => {
    const [requirements, setRequirements] = useState([]);
    const [csrLogs, setCsrLogs] = useState([]);
    const [loadingCreate, setLoadingCreate] = useState(false);
    const [loadingUpdate, setLoadingUpdate] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [loadingFetchSpecs, setLoadingFetchSpecs] = useState(false);
    const [loadingScore, setLoadingScore] = useState(false);
    const [loadingCsrLogs, setLoadingCsrLogs] = useState(false);
    const [error, setError] = useState(null);

    // Get all system requirements
    const getAllSystemRequirements = useCallback(async () => {
        setError(null);

        const promise = (async () => {
            const path = `/computers/system-requirements`;
            const response = await callApi(path, 'get');

            if (!response.ok) {
                throw new UserFriendlyError(await analyzeApiResponse(response));
            }

            const results = await response.json();
            setRequirements(results);
            return results;
        })();

        await showToastPromise(promise, {
            loading: 'Loading requirements...',
            success: 'Requirements loaded!'
        }).catch((err) => setError(convertError(err)));
    }, []);

    // Fetch & save computer specs
    const fetchAndSaveComputerSpecs = async () => {
        setLoadingFetchSpecs(true);
        setError(null);

        const promise = (async () => {
            const path = `/computers/specs/fetch-and-save`;
            const response = await callApi(path, 'get');

            if (!response.ok) {
                throw new UserFriendlyError(await analyzeApiResponse(response));
            }

            return response.json();
        })();

        try {
            await showToastPromise(promise, {
                loading: 'Fetching computer specs...',
                success: 'Computer specs fetched and saved!'
            });

            // ✅ Wait for updated data to load
            await getAllSystemRequirements();
        } catch (err) {
            setError(convertError(err));
        } finally {
            setLoadingFetchSpecs(false);
        }
    };

    // Get system score by computerId
    const getSystemScoreByComputerId = async (computerId) => {
        setLoadingScore(true);
        setError(null);

        const promise = (async () => {
            const path = `/computers/${computerId}/score`;
            const response = await callApi(path, 'get');

            if (!response.ok) {
                throw new UserFriendlyError(await analyzeApiResponse(response));
            }

            return response.json();
        })();

        const result = await showToastPromise(promise, {
            loading: 'Calculating system score...',
            success: 'System score fetched!'
        })
            .catch((err) => {
                setError(convertError(err));
                return null;
            })
            .finally(() => setLoadingScore(false));

        return result;
    };

    // Create a new system requirement
    const createSystemRequirement = async (requirementData) => {
        setLoadingCreate(true);
        setError(null);

        const promise = (async () => {
            const path = `/computers/system-requirements`;
            const response = await callApi(path, 'post', requirementData);

            if (!response.ok) {
                throw new UserFriendlyError(await analyzeApiResponse(response));
            }

            const result = await response.json();
            setRequirements((prev) => [...prev, result]);
            return result;
        })();

        await showToastPromise(promise, {
            loading: 'Creating requirement...',
            success: 'Requirement created!'
        })
            .catch((err) => setError(convertError(err)))
            .finally(() => setLoadingCreate(false));
    };

    // Update system requirement score
    const updateSystemRequirementScore = async (
        requirementId,
        updatedScore
    ) => {
        const previousRequirements = requirements;
        setLoadingUpdate(true);
        setError(null);

        // Optimistic UI update
        setRequirements((prev) =>
            prev.map((req) =>
                req.id === requirementId ? { ...req, score: updatedScore } : req
            )
        );

        const promise = (async () => {
            const path = `/computers/system-requirements/${requirementId}/score`;
            const response = await callApi(path, 'patch', {
                score: updatedScore
            });

            if (!response.ok) {
                throw new UserFriendlyError(await analyzeApiResponse(response));
            }

            return response.json();
        })();

        await showToastPromise(promise, {
            loading: 'Updating requirement...',
            success: 'Requirement updated!'
        })
            .catch((err) => {
                // Rollback if error
                setRequirements(previousRequirements);
                setError(convertError(err));
            })
            .finally(() => setLoadingUpdate(false));
    };

    // Delete a system requirement
    const deleteSystemRequirement = async (requirementId) => {
        setLoadingDelete(true);
        setError(null);

        const promise = (async () => {
            const path = `/computers/system-requirements/${requirementId}`;
            const response = await callApi(path, 'delete');

            if (!response.ok) {
                throw new UserFriendlyError(await analyzeApiResponse(response));
            }

            // After successful delete, remove from local state
            setRequirements((prev) =>
                prev.filter((req) => req.id !== requirementId)
            );

            return true;
        })();

        await showToastPromise(promise, {
            loading: 'Deleting requirement...',
            success: 'Requirement deleted!'
        })
            .catch((err) => setError(convertError(err)))
            .finally(() => setLoadingDelete(false));
    };

    // Fetch CSR email logs
    const fetchCsrEmailLogs = useCallback(
        async ({
            officeName = '',
            startDate = '',
            endDate = '',
            page = 1,
            limit = 20,
            sortBy = 'sentAt:desc'
        } = {}) => {
            setLoadingCsrLogs(true);
            setError(null);

            const params = new URLSearchParams();
            if (officeName) params.append('officeName', officeName);
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            params.append('page', page);
            params.append('limit', limit);
            params.append('sortBy', sortBy);

            const promise = (async () => {
                const path = `/computers/logs?${params.toString()}`;
                const response = await callApi(path, 'get');

                if (!response.ok) {
                    throw new UserFriendlyError(
                        await analyzeApiResponse(response)
                    );
                }

                const data = await response.json();
                setCsrLogs(data);
                return data;
            })();

            await showToastPromise(promise, {
                loading: 'Loading email logs...',
                success: 'Email logs loaded!'
            })
                .catch((err) => setError(convertError(err)))
                .finally(() => setLoadingCsrLogs(false));
        },
        []
    );

    const resetRequirements = () => {
        setRequirements([]);
        setError(null);
    };

    const resetCsrLogs = () => {
        setCsrLogs([]);
    };

    return {
        requirements,
        csrLogs: csrLogs || { results: [], totalPages: 1 },
        loading: {
            create: loadingCreate,
            update: loadingUpdate,
            delete: loadingDelete,
            fetchSpecs: loadingFetchSpecs,
            score: loadingScore,
            csrLogs: loadingCsrLogs
        },
        error,
        getAllSystemRequirements,
        createSystemRequirement,
        updateSystemRequirementScore,
        deleteSystemRequirement,
        fetchAndSaveComputerSpecs,
        getSystemScoreByComputerId,
        fetchCsrEmailLogs,
        resetRequirements,
        resetCsrLogs
    };
};

export default useSystemRequirements;
